// src/pages/PurchaseReceipt.jsx
import * as React from "react";
import axios from "axios";
import Header from "../components/Header";
import SimpleTable from "../components/SimpleTable";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Divider,
  TextField,
  Button,
  Snackbar,
  Alert,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { tokens } from "../theme";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

/* ---------- helpers ---------- */
const show = (v) =>
  v === null || v === undefined || v === "" ? "-" : String(v).trim();

const ETB = new Intl.NumberFormat("en-ET", {
  style: "currency",
  currency: "ETB",
  maximumFractionDigits: 2,
});

function parseNum(n) {
  if (n == null) return 0;
  if (typeof n === "number") return n;
  const x = String(n).replace(/,/g, "");
  const v = Number(x);
  return Number.isFinite(v) ? v : 0;
}
function sameDay(dateStr, ymd) {
  if (!dateStr || !ymd) return false;
  const d = new Date(dateStr);
  const [Y, M, D] = ymd.split("-").map((t) => Number(t));
  return d.getFullYear() === Y && d.getMonth() + 1 === M && d.getDate() === D;
}

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});
async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function PurchaseReceipt() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // supplier & date controls
  const [suppliers, setSuppliers] = React.useState([]);
  const [supplierId, setSupplierId] = React.useState("");
  const [purchaseDate, setPurchaseDate] = React.useState(""); // YYYY-MM-DD

  // data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // fetch suppliers
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getJSON("/api/customer", {
        type: "supplier",
      });
      if (cancelled) return;
      if (error) {
        setSuppliers([]);
        setToast({ open: true, msg: "Failed to load suppliers", sev: "error" });
      } else {
        setSuppliers(Array.isArray(data) ? data : []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // fetch purchases when supplier & date are selected
  React.useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!supplierId || !purchaseDate) return;

      setLoading(true);
      const { data, error } = await getJSON("/api/purchase", {
        supplierId,
        date: purchaseDate,
      });
      setLoading(false);
      if (cancelled) return;

      if (error) {
        setRows([]);
        setToast({
          open: true,
          msg: "Failed to load purchases",
          sev: "error",
        });
        return;
      }

      const arr = Array.isArray(data) ? data : [];
      const filtered = arr.filter(
        (row) =>
          String(row.supplierId) === String(supplierId) &&
          sameDay(row.purchaseDate, purchaseDate)
      );

      const tableRows = filtered.map((p) => {
        const qty = parseNum(p.quantity);
        const unitPrice =
          p.Item?.unitPrice != null
            ? parseNum(p.Item.unitPrice)
            : parseNum(p.unitPrice);
        const amount = qty * unitPrice;
        return {
          id: p.id,
          itemName: p.Item?.name ?? "",
          unit: p.Item?.unit ?? "",
          quantity: qty,
          unitPrice,
          amount,
          exciseTaxValue: parseNum(p.exciseTax), // used for Yes/No column
          purchaseDate: p.purchaseDate,
          warehouseName: p.Warehouse?.name ?? "",
          customerName: p.Customer?.name ?? "Seblu Derese",
          customerTin: p.Customer?.tin ?? p.Customer?.TIN ?? "",
          customerVat: p.Customer?.vatRegNo ?? p.Customer?.vat ?? "",
          customerAddress: p.Customer?.address ?? "",
        };
      });

      setRows(tableRows);
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [supplierId, purchaseDate]);

  // current supplier (for header and From card)
  const currentSupplier =
    suppliers.find((s) => String(s.id) === String(supplierId)) || null;

  const firstRow = rows[0];
  const toName = firstRow?.customerName || "Seblu Derese";
  const toTin = firstRow?.customerTin || "0014282805";
  const toVat = firstRow?.customerVat || "";
  const toAddress = firstRow?.customerAddress || "Addis Ababa";

  // totals (rules you specified)
  const totals = React.useMemo(() => {
    const exciseItems = rows.filter((r) => parseNum(r.exciseTaxValue) > 0);
    const nonExciseItems = rows.filter((r) => parseNum(r.exciseTaxValue) === 0);

    const sum = (arr, field) =>
      arr.reduce((acc, x) => acc + parseNum(x[field]), 0);

    const totalBefore = sum(exciseItems, "amount");
    const exciseTax25 = totalBefore * 0.25; // tax portion only
    const totalExclude = sum(nonExciseItems, "amount");

    const totalBeforeVAT = totalBefore + exciseTax25 + totalExclude;
    const vat15 = totalBeforeVAT * 0.15;
    const grandTotal = totalBeforeVAT + vat15;

    return {
      totalBefore,
      exciseTax25,
      totalExclude,
      totalBeforeVAT,
      vat15,
      grandTotal,
    };
  }, [rows]);

  // columns for SimpleTable
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "itemName", headerName: "Item", flex: 1, minWidth: 180 },
    { field: "unit", headerName: "Unit", width: 110 },
    {
      field: "quantity",
      headerName: "Qty",
      width: 110,
      type: "number",
      headerAlign: "left", // ← left-align header
      align: "left", // ← left-align cells (your request)
      valueFormatter: (p) => p.value ?? 0,
    },
    {
      field: "unitPrice",
      headerName: "Unit Price",
      width: 140,
      valueFormatter: (params) => ETB.format(params.value ?? 0),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 160,
      valueFormatter: (params) => ETB.format(params.value ?? 0),
    },
    {
      field: "excise",
      headerName: "Excise",
      width: 110,
      sortable: false,
      filterable: true,
      valueGetter: (params) =>
        parseNum(params.row.exciseTaxValue) > 0 ? "Yes" : "No",
    },
  ];

  // grid look (theme-consistent like your ViewRole)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  const documentTitle = "VALUE ADDED TAX CASH SALES INVOICE";
  const companyName = currentSupplier?.name || "—";

  const handlePrintOrPdf = () => window.print();

  return (
    <>
      {/* Print-only CSS to hide sidebar and fit to a single A4 page */}
      <style>{`
@media print {
  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  body {
    background: #ffffff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Hide everything outside the receipt area */
  body * {
    visibility: hidden !important;
  }

  #receipt-print-area,
  #receipt-print-area * {
    visibility: visible !important;
  }

  /* Only print the receipt area */
  #receipt-print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 190mm;
    margin: 0 auto;
    padding: 0 !important;
    background: white !important;
  }

  /* Hide navbars, drawers, controls */
  .MuiDrawer-root,
  .MuiAppBar-root,
  .MuiToolbar-root,
  .no-print,
  aside,
  nav,
  header,
  footer {
    display: none !important;
    visibility: hidden !important;
  }

  /* Force "From" and "To" to be side by side */
  #receipt-print-area .MuiGrid-container {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: flex-start !important;
    gap: 16px !important;
    flex-wrap: nowrap !important;
  }

  #receipt-print-area .MuiGrid-item {
    flex: 1 1 48% !important;
    max-width: 48% !important;
  }

   #receipt-print-area .MuiPaper-root {
    background: #fff !important;
  }
  #receipt-print-area { color: #000 !important; }
  #receipt-print-area * { color: #000 !important; }
  
  /* Ensure the table and totals are fully visible */
  .MuiDataGrid-virtualScroller {
    height: auto !important;
    overflow: visible !important;
  }

  .MuiDataGrid-footerContainer,
  .MuiDataGrid-toolbarContainer {
    display: none !important;
  }
    
}
`}</style>

      <Box m="20px">
        <Header
          title="PURCHASE RECEIPT"
          subtitle="Generate receipt by supplier and date"
        />

        {/* Back Button */}
        <Box
          display="flex"
          alignItems="center"
          className="no-print"
          mt={1}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/purchase/view")}
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              py: 0.8,
            }}
          >
            Back
          </Button>
        </Box>

        {/* Controls */}
        <Box
          className="no-print"
          sx={{
            mt: 3,
            mb: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: colors.primary[400],
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="filled">
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={supplierId}
                  onChange={(e) => setSupplierId(e.target.value)}
                >
                  {suppliers.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="Purchase Date"
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                fullWidth
                variant="filled"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={5}
              sx={{ textAlign: { xs: "left", md: "right" } }}
            >
              <Button
                variant="outlined"
                onClick={handlePrintOrPdf}
                sx={{ mr: 1 }}
              >
                Download PDF
              </Button>
              <Button variant="outlined" onClick={handlePrintOrPdf}>
                Print
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Centered header */}
        <div id="receipt-print-area">
          <Box sx={{ textAlign: "center", mt: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {companyName}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {documentTitle}
            </Typography>
            {purchaseDate ? (
              <Typography variant="caption" color="text.secondary">
                Date: {purchaseDate}
              </Typography>
            ) : null}
          </Box>

          {/* From / To simple columns with spacing (dark-mode safe) */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 1, color: theme.palette.text.primary }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  From
                </Typography>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Name
                  </Typography>
                  <Typography color="text.secondary">
                    {show(currentSupplier?.name)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Address
                  </Typography>
                  <Typography color="text.secondary">
                    {show(currentSupplier?.address)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    VAT Reg No
                  </Typography>
                  <Typography color="text.secondary">
                    {show(currentSupplier?.vatRegNo || currentSupplier?.vat)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    TIN
                  </Typography>
                  <Typography color="text.secondary">
                    {show(currentSupplier?.tin || currentSupplier?.TIN)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ p: 1, color: theme.palette.text.primary }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  To
                </Typography>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Name
                  </Typography>
                  <Typography color="text.secondary">{show(toName)}</Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Address
                  </Typography>
                  <Typography color="text.secondary">
                    {show(toAddress)}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Customer VAT No
                  </Typography>
                  <Typography color="text.secondary">{show(toVat)}</Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Typography sx={{ width: 130, fontWeight: 600 }}>
                    Customer TIN
                  </Typography>
                  <Typography color="text.secondary">{show(toTin)}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Table */}
          <Box m="40px 0 0 0">
            <SimpleTable
              rows={rows}
              columns={columns}
              loading={loading}
              height="60vh"
              getRowId={(r) => r.id}
              sx={gridSx}
            />
          </Box>

          {/* Totals block */}
          <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
            <Grid item xs={12} md={5} lg={4}>
              <Paper variant="outlined" sx={{ p: 2 }} className="print-totals">
                <TotalRow label="Total Before" value={totals.totalBefore} />
                <TotalRow
                  label="Total including Excise tax 25%"
                  value={totals.exciseTax25}
                />
                <TotalRow
                  label="Total exclude Excise tax"
                  value={totals.totalExclude}
                />
                <Divider sx={{ my: 1 }} />
                <TotalRow label="Total" value={totals.totalBeforeVAT} />
                <TotalRow label="VAT 15%" value={totals.vat15} />
                <Divider sx={{ my: 1 }} />
                <TotalRow
                  label="Grand Total"
                  value={totals.grandTotal}
                  strong
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  * “Total Before” sums amounts for items with excise; “Total
                  including Excise tax 25%” shows the tax portion only.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </div>
      </Box>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function KV({ label, value }) {
  return (
    <Box sx={{ mb: 1.2 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 0.8 }} />
    </Box>
  );
}
function TotalRow({ label, value, strong = false }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.7 }}>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: strong ? 700 : 600, fontFeatureSettings: "'tnum' 1" }}
      >
        {ETB.format(value || 0)}
      </Typography>
    </Box>
  );
}
